/* DRAFT — PENDING LEGAL REVIEW */
import React from "react";
import PageShell from "../../components/layout/PageShell";
import Container from "../../components/primitives/Container";
import Section from "../../components/primitives/Section";
import Eyebrow from "../../components/primitives/Eyebrow";
import Reveal from "../../components/primitives/Reveal";
import SeoHead from "../../components/seo/SeoHead";
import DraftBanner from "../../components/legal/DraftBanner";

const H2 = ({ children }) => (
  <h2
    className="mt-12 font-serif font-normal text-navy text-balance"
    style={{ fontSize: "clamp(26px, 3vw, 36px)", lineHeight: 1.2, letterSpacing: "-0.005em" }}
  >
    {children}
  </h2>
);

const P = ({ children, ...rest }) => (
  <p
    {...rest}
    className="mt-6 font-sans text-[16px] sm:text-[16.5px] leading-[1.75] text-navy/80 max-w-prose"
  >
    {children}
  </p>
);

const CookiePolicy = () => {
  return (
    <PageShell>
      <SeoHead
        title="Cookie Policy"
        description="How Quantum US Capital Partners uses cookies and comparable technologies on quscap.com."
        path="/legal/cookies"
        noIndex
      >
        <meta name="x-document-status" content="draft-pending-legal-review" />
      </SeoHead>

      <Section tone="light" className="pt-28 md:pt-32 lg:pt-36 pb-20 md:pb-24">
        <Container>
          <Reveal className="max-w-3xl">
            <DraftBanner />
            <div className="mt-8">
              <Eyebrow tone="muted">Legal</Eyebrow>
            </div>
            <h1
              className="mt-8 font-serif font-normal text-navy text-balance"
              data-testid="cookies-title"
              style={{ fontSize: "clamp(40px, 5.5vw, 80px)", lineHeight: 1.05, letterSpacing: "-0.01em" }}
            >
              Cookie Policy.
            </h1>
            <span aria-hidden="true" className="mt-10 block h-px w-12 bg-gold" />
            <p
              className="mt-10 font-sans uppercase text-navy/55"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              data-testid="cookies-effective"
            >
              Effective February 2026
            </p>
          </Reveal>

          <Reveal className="mt-10 max-w-3xl">
            <P>
              This Cookie Policy describes how Quantum US Capital Partners LLC
              (&ldquo;Quantum&rdquo;) uses cookies and comparable technologies
              on the quscap.com website. It should be read together with our{" "}
              <a
                href="/legal/privacy"
                className="text-navy underline decoration-gold underline-offset-4 hover:text-gold transition-colors"
              >
                Privacy Policy
              </a>
              .
            </P>

            <H2>What Cookies Are</H2>
            <P>
              Cookies are small text files placed on your device by websites
              you visit. They allow a site to remember your actions and
              preferences over time. We also use comparable technologies such
              as local storage where more appropriate to the function
              performed.
            </P>

            <H2>Categories We Use</H2>
            <P>
              <strong className="font-medium text-navy">Strictly Necessary.</strong>{" "}
              Required for the Site to function &mdash; for example, to
              maintain a secure administrative session or to remember your
              cookie preference itself. These are always active and cannot be
              refused without rendering parts of the Site unusable.
            </P>
            <P>
              <strong className="font-medium text-navy">Analytics.</strong>{" "}
              Provided by Google Analytics 4 (GA4). Used in aggregate to
              understand how visitors engage with our material. Loaded only
              after you accept them via the cookie notice. If you decline, no
              analytics cookies are placed and no analytics data is collected.
            </P>
            <P>
              We do not use advertising, targeting, or cross-site tracking
              cookies. We do not sell information collected via cookies.
            </P>

            <H2>Managing Your Preferences</H2>
            <P>
              Your cookie decision is recorded in your browser&rsquo;s local
              storage under the key <code className="font-mono text-navy/90">quantum_consent_v1</code>{" "}
              and respected on subsequent visits. You may revisit your
              decision at any time via the{" "}
              <em className="italic">Cookie Preferences</em> link in the
              footer, or by clearing your browser storage.
            </P>
            <P>
              Most browsers also allow you to block or delete cookies through
              their own settings. Doing so may affect the operation of parts
              of the Site.
            </P>

            <H2>Third-Party Cookies</H2>
            <P>
              Where you consent to analytics, Google Analytics places its own
              cookies. Google&rsquo;s use of the resulting information is
              governed by its own policies. We use aggregated,
              non-identifying reports and do not construct individual visitor
              profiles from this data.
            </P>

            <H2>Changes to this Policy</H2>
            <P>
              We may update this Cookie Policy from time to time to reflect
              changes in the technologies we use or in applicable law. The
              effective date above will be revised accordingly.
            </P>

            <H2>Contact</H2>
            <P>
              Inquiries regarding this Cookie Policy may be addressed to the
              Office of the Chairman at{" "}
              <a
                href="mailto:chairman@quscap.com"
                className="text-navy underline decoration-gold underline-offset-4 hover:text-gold transition-colors"
                data-testid="cookies-contact-email"
              >
                chairman@quscap.com
              </a>
              .
            </P>
          </Reveal>
        </Container>
      </Section>
    </PageShell>
  );
};

export default CookiePolicy;
